import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import { config } from "@/configuration/app/app-config"
import {
    TransportationReport,
    TransportationReportResult,
    ShipmentMaterials,
    ShipmentEmpties,
} from "@/model/transportation-report/transportation-report"
import {
    TransportationReportRequestRest,
    TransportationReportResponseRest,
    ShipmentMaterialsRest,
    ShipmentEmptiesRest,
    transportationReportResponseRestSchema
} from "./TransportationReport.model"
import moment from "moment"
import { accessToken } from "@/configuration/token/accessToken"

const TRANSPORTATION_REPORT_API_TAG = "TRANSPORTATION_REPORT"

const transportationReportBaseUrl = config.shipmentTrackingApi.baseUrl

const toShipmentType = (src: ShipmentMaterials | ShipmentEmpties): ShipmentMaterialsRest | ShipmentEmptiesRest => {
    if (src.type === "MATERIALS") {
        return {
            shipment_number: src.shipmentNumber,
            ship_from: {
                duns: src.shipFrom.duns,
                supplier_code: src.shipFrom.supplierCode,
            },
            volume: src.volume ? src.volume : undefined,
            type: "MATERIALS",
            ship_to: {
                duns: src.shipTo.duns,
                plant_code: src.shipTo.plantCode,
            }
        }
    } else {
        return {
            shipment_number: src.shipmentNumber,
            ship_from: {
                duns: src.shipFrom.duns,
                supplier_code: src.shipFrom.supplierCode,
            },
            volume: src.volume ? src.volume : undefined,
            type: "EMPTIES",
            ship_to: {
                duns: src.shipTo.duns,
                supplier_code: src.shipTo.supplierCode,
            }
        }
    }
}

const toTransportationReportRest = (src: TransportationReport): TransportationReportRequestRest => ({
    transport_details: {
        freight_forwarder: {
            duns: src.transportDetails.freightForwarder.duns,
            supplier_code: src.transportDetails.freightForwarder.supplierCode,
        },
        transport_mode: {
            type: src.transportDetails.transportMode.type,
            means_of_transport: src.transportDetails.transportMode.meansOfTransport,
            license_plate: src.transportDetails.transportMode.licensePlate,
            fuel_type: src.transportDetails.transportMode.fuelType,
        },
        bordero_number: src.transportDetails.borderoNumber,
        access_code: src.transportDetails.accessCode || undefined,
        loading : {
            loaded_at: moment(src.transportDetails.loading.loadedAt).format("YYYY-MM-DD"),
            duns: src.transportDetails.loading.duns,
            supplier_code: src.transportDetails.loading.supplierCode,
        },
        unloading: {
            unloaded_at: moment(src.transportDetails.unloading.unloadedAt).format("YYYY-MM-DD"),
            duns: src.transportDetails.unloading.duns,
            supplier_code: src.transportDetails.unloading.supplierCode,
        },
    },
    shipments: src.shipments.map((shipment) => toShipmentType(shipment)),
})

const toTransportationReport = (src: TransportationReportResponseRest): TransportationReportResult => ({
    reportId: src.report_id,
})

export const transportationReportApi = createApi({
    reducerPath: "transportationReportApi",
    tagTypes: [TRANSPORTATION_REPORT_API_TAG],
    baseQuery: fetchBaseQuery({
        baseUrl: transportationReportBaseUrl,
        prepareHeaders: (headers) => {
            headers.set("authorization", `Bearer ${accessToken.getAccessToken()}`)
            headers.set("content-type", "application/json")
            headers.set("accept", "application/json")
            return headers
        },
    }),
    endpoints: (builder) => ({
        postTransportationReport: builder.mutation<TransportationReportResult, TransportationReport>({
            query: (report) => ({
                url: "/transportation-reports",
                method: "POST",
                body: toTransportationReportRest(report),
            }),
            transformResponse: (response: unknown) => {
                const transportationReportRest = transportationReportResponseRestSchema.parse(response)
                return toTransportationReport(transportationReportRest)
            },
            invalidatesTags: [{ type: TRANSPORTATION_REPORT_API_TAG }],
        }),
    }),
})

export const {
    usePostTransportationReportMutation,
} = transportationReportApi
